'use client';

import type {
  CrossPostProgress,
  CrossPostStage
} from '@shared/all/domains/socket';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import socket from '@shared/utils/socket/socket';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useState
} from 'react';

import { client } from '../utils/trpc';

export const STAGE_LABEL: Record<CrossPostStage, string> = {
  queued: 'Queued',
  fetching: 'Preparing media',
  uploading: 'Uploading',
  publishing: 'Publishing',
  processing: 'Processing',
  posted: 'Done',
  failed: 'Failed'
};

export type ActiveCrossPost = {
  crossPostId: number;
  postUuid: string;
  platform: string;
  stage: CrossPostStage;
  percent: number;
};

type ContextValue = { active: ActiveCrossPost[] };

const CrossPostProgressContext = createContext<ContextValue>({ active: [] });

const TERMINAL: CrossPostStage[] = ['posted', 'failed'];

// App-wide cross-post progress: membership comes from the server (survives
// refresh), the socket overlays the live stage/percent.
export const CrossPostProgressProvider = ({ children }: PropsWithChildren) => {
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['activeCrossPosts'],
    queryFn: () =>
      unwrapFromResult(client.crosspost.getActiveCrossPosts.query()),
    retry: false
  });

  const [live, setLive] = useState<
    Record<number, { stage: CrossPostStage; percent: number }>
  >({});

  useEffect(() => {
    const handler = (p: CrossPostProgress) => {
      // Terminal: drop the overlay, re-pull the list, refresh the post's card.
      if (TERMINAL.includes(p.stage)) {
        setLive((prev) => {
          const next = { ...prev };
          delete next[p.crossPostId];
          return next;
        });
        void queryClient.invalidateQueries({ queryKey: ['activeCrossPosts'] });
        void queryClient.invalidateQueries({
          queryKey: ['crossPostsForPost', p.postUuid]
        });
        return;
      }

      setLive((prev) => ({
        ...prev,
        [p.crossPostId]: { stage: p.stage, percent: p.percent }
      }));

      // A freshly queued cross-post isn't in the server list yet — pull it in.
      if (p.stage === 'queued') {
        void queryClient.invalidateQueries({ queryKey: ['activeCrossPosts'] });
      }
    };

    socket.on('crosspostProgress', handler);
    return () => {
      socket.off('crosspostProgress', handler);
    };
  }, [queryClient]);

  const active: ActiveCrossPost[] = (data?.active ?? []).map((row) => {
    const overlay = live[row.crossPostId];
    return {
      crossPostId: row.crossPostId,
      postUuid: row.postUuid,
      platform: row.platform,
      stage:
        overlay?.stage ?? (row.status === 'POSTING' ? 'uploading' : 'queued'),
      percent: overlay?.percent ?? (row.status === 'POSTING' ? 40 : 3)
    };
  });

  return (
    <CrossPostProgressContext.Provider value={{ active }}>
      {children}
    </CrossPostProgressContext.Provider>
  );
};

export function useCrossPostProgress(): ContextValue {
  return useContext(CrossPostProgressContext);
}
